import { FC, useContext, useEffect, useRef, useState } from "react";

import { FormType } from "@simplyk/common";

import { FormV2Provider } from "@/canvas/features/FormV2/contexts/FormV2Provider";
import { FormThemeProvider } from "@/canvas/features/FormV2/FormThemeProvider";
import { sendEmbedStepChangedMessage } from "@/canvas/features/FormV2/helpers/dynamicEmbedMessaging";
import { PreviewTicketingData } from "@/canvas/features/FormV2/hooks/useTicketingFormState";
import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { StripeProvider } from "@/common/providers/StripeProvider";
import { DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "@/common/types/trpc";

export type FormProvidersProps = {
  children: React.ReactNode | ((currentStep: FormStepKey) => React.ReactNode);
  isEmbed: boolean;
} & (
  | { type: FormType.Ticketing; formObject: TicketingFrontendPageOutput; previewTicketingData?: PreviewTicketingData }
  | { type: FormType.DonationForm; formObject: DonationFormFrontendPageOutput; previewTicketingData?: never }
);

export const FormProviders: FC<FormProvidersProps> = ({
  children,
  type,
  formObject,
  isEmbed,
  previewTicketingData,
}) => {
  const isDarkMode = formObject.isDarkMode;
  const { setShouldFetchTipSuggestions } = useContext(FrontendFormContext);

  const [currentStep, setCurrentStep] = useState<FormStepKey>("selection");
  const previousStepRef = useRef<FormStepKey | null>(null);

  const handleSetCurrentStep = (step: FormStepKey) => {
    setCurrentStep(step);
    setShouldFetchTipSuggestions(step === "payment");
  };

  // Parent uses `step` to drop the iframe height animation on payment.
  useEffect(() => {
    if (isEmbed && previousStepRef.current !== null && previousStepRef.current !== currentStep) {
      sendEmbedStepChangedMessage(currentStep);
    }
    previousStepRef.current = currentStep;
  }, [currentStep, isEmbed]);

  return (
    <FormThemeProvider baseHexColor={formObject.color} mode={isDarkMode ? "dark" : "light"}>
      <FormV2Provider
        type={type}
        formObject={formObject}
        currentStep={currentStep}
        setCurrentStep={handleSetCurrentStep}
        isEmbed={isEmbed}
        previewTicketingData={previewTicketingData}
      >
        <StripeProvider isEmbed={isEmbed}>
          {typeof children === "function" ? children(currentStep) : children}
        </StripeProvider>
      </FormV2Provider>
    </FormThemeProvider>
  );
};
